<!-- Top header with breadcrumbs -->
<div class="info-header">
  <div class="container-fluid lh-container-fluid">
    <div class="flex">
      <div *ngIf="booking" class="heading-5">
        <a class="breadcrumb-link" [routerLink]="'/dashboard'">{{ booking.shopName }}</a>
        <span class="breadcrumb-divider">></span>
        <a class="breadcrumb-link" (click)="goBackToBookingList()">{{ 'BOOKINGS' | translate }}</a>
        <span class="breadcrumb-divider">></span>{{ booking._id }}
      </div>
    </div>
  </div>
</div>

<div class="container-fluid lh-container-fluid main-container">
  <!-- In-page header with booking id and action buttons -->
  <div class="booking-header">
    <div class="booking-id">
      <div class="booking-id-label">{{ 'BOOKING_ID' | translate }}</div>
      <div class="booking-id-value">{{ bookingId }}</div>
    </div>
    <div class="booking-actions">
      <!-- Refresh button -->
      <button
        id="refreshBooking"
        class="btn btn-md btn-default btn-refresh"
        [disabled]="isLoading"
        (click)="loadBooking(true)"
      >
        <i class="icon-refresh" *ngIf="!isLoading"></i>
        <lh-shared-ui-spinner [visible]="isLoading"></lh-shared-ui-spinner>
      </button>

      <!-- Action button -->
      <button
        class="btn btn-md"
        [class.btn-primary]="!isCheckedIn"
        [class.btn-success]="isCheckedIn"
        [disabled]="isLoading"
        (click)="requestAction(currentAction)"
        *ngIf="currentAction"
      >
        {{ currentAction.callToAction | translate }}
      </button>
    </div>
  </div>

  <!-- Booking badges -->
  <div class="booking-badges" *ngIf="booking">
    <span
      class="booking-badge"
      [class.success]="booking.status === 'PAID' && !booking.paidDirectly"
      [class.warning]="booking.status === 'PAID' && booking.paidDirectly"
      [class.danger]="booking.status === 'CHECKED_OUT'"
      [class.primary]="booking.status === 'CHECKED_IN'"
      [hidden]="booking.status === 'CANCELLED'"
      [tooltip]="booking | booking: 'statusVerbose'"
      >{{ booking | booking: 'status' }}</span
    >
    <span
      class="booking-badge info"
      [tooltip]="'WALK_IN_EXPLANATION' | translate"
      *ngIf="booking.isWalkIn && !booking.isZeroPaymentBooking"
      >{{ 'WALK_IN' | translate }}</span
    >
    <span class="booking-badge info" [tooltip]="'GUEST_EXPLANATION' | translate" *ngIf="booking.isZeroPaymentBooking">{{
      'GUEST' | translate
    }}</span>
    <span
      class="booking-badge info"
      [tooltip]="'MULTI_DAY_BOOKING_EXPLANATION' | translate"
      *ngIf="booking.metadata && booking.metadata.lh_storage_days > 1"
      >{{ booking.metadata.lh_storage_days }} {{ 'DAYS' | translate }}</span
    >
    <span
      class="booking-badge yellow"
      [tooltip]="'BOOKING_INCIDENT_EXPLANATION' | translate"
      *ngIf="booking.incidentReported"
      >{{ 'INCIDENT' | translate }}</span
    >
    <span
      class="booking-badge alert"
      [class.alert-success]="!isDirectPaymentRequired"
      [class.alert-danger]="isDirectPaymentRequired && !booking.paidDirectly"
      [class.alert-warning]="isDirectPaymentRequired && booking.paidDirectly"
      [tooltip]="bookingAmountTooltip | translate"
      *ngIf="isBookingAmountVisible"
      >{{ bookingAmount | price: currency : 2 : false }}</span
    >
  </div>

  <hr />

  <!-- Error message -->
  <div class="alert alert-warning mt-2" *ngIf="error">
    {{ 'STORAGE_LOCATION_LOAD_BOOKING_ERROR' | translate }}
  </div>

  <div class="row">
    <!-- Incidents section -->
    <div class="booking-incidents col-12 col-sm-6" *ngIf="booking && booking.incidentReported">
      <h4>{{ 'REPORTED_INCIDENTS' | translate }}</h4>

      <div class="booking-incident" *ngFor="let incident of booking.incidents">
        <div class="incident-title">
          {{ incident.incidentType | translate }}
        </div>
        <div class="incident-date">{{ incident.reportedAt | lhMoment: 'fromNow' }}</div>
        <div class="incident-comments" *ngIf="incident.comments">"{{ incident.comments }}"</div>
        <div class="incident-image" *ngIf="incident.images && incident.images[0]">
          <img [imageName]="incident.images[0]" imageSize="medium" lhImage />
        </div>
      </div>

      <hr class="d-block d-sm-none" />
    </div>

    <!-- Luggage photos section -->
    <div class="booking-luggage col-12 col-sm-6" *ngIf="booking?.luggage?.images[0]">
      <h4>{{ 'LUGGAGE_PHOTOS' | translate }}</h4>

      <div class="luggage-image" (click)="showLuggageImage(booking.luggage.images[0])">
        <div
          class="background-image"
          [remoteImage]="booking.luggage.images[0]"
          [remoteImageSize]="'medium'"
          lhBackgroundImage
        ></div>
      </div>

      <hr class="d-block d-sm-none" />
    </div>

    <!-- Customer info section -->
    <div class="booking-customer col-12 col-sm-6" *ngIf="showContactDetails">
      <h4>{{ 'CUSTOMER' | translate }}</h4>

      <div class="contact-card">
        <div class="contact-image">{{ userName[0] }}</div>
        <div class="contact-name">{{ userName }}</div>
        <div class="contact-info" *ngIf="booking.userEmail">
          <a href="mailto:{{ booking.userEmail }}">{{ booking.userEmail }}</a>
        </div>
        <div class="contact-info" *ngIf="booking.userPhone">
          <a href="tel:{{ booking.userPhone }}">{{ booking.userPhone | phoneNumber }}</a>
        </div>
        <div class="contact-info" *ngIf="!booking.userEmail && !booking.userPhone">
          {{ 'NO_CONTACT_INFO_AVAILABLE' | translate }}
        </div>
      </div>
    </div>

    <!-- Storage timer section -->
    <div class="booking-timer col-12 col-sm-6">
      <h4>{{ 'STORAGE_TIMER' | translate }}</h4>

      <div class="storage-timer">
        <lh-storage-timer
          [booking]="booking"
          [isCheckInSession]="isCheckInSession"
          [compactMode]="true"
          [largeDigits]="!isSmallScreen"
        ></lh-storage-timer>
      </div>
    </div>

    <!-- Booking activity section -->
    <div class="booking-events col-12 col-sm-6" *ngIf="bookingEvents && bookingEvents.length > 0">
      <h4>{{ 'BOOKING_ACTIVITY' | translate }}</h4>

      <div class="booking-event" *ngFor="let event of bookingEvents">
        <lh-shared-ui-user-profile-image
          [name]="event.actor"
          size="lg"
          [profileBackgroundColors]="profileBackgroundColors"
        />
        <div class="booking-event-panel" [class.system]="event.type === 'system'">
          <div class="actor">{{ event.actor }}</div>
          <div class="text">{{ event.text | translate | lowercase }}</div>
          <div class="date">{{ event.date | lhMoment: 'calendarTimeShort' | uppercaseFirstLetter }}</div>
        </div>
      </div>
    </div>
  </div>
</div>
